/// <reference types="@types/google.maps" />
import {
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChange,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { DriverStatusConstants, IGeoCoords, IMapRoute, MapsService, MiniDriver, ReservationService, RideRequestDetail } from '@uzurv/clara';
import { DistanceConversionProvider, ReservationStatusNameConstants, TimeConversionProvider, isNullOrUndefined } from '@uzurv/core';
import { APP_CONFIGURATION, AppConfiguration } from 'projects/uzurv/src/app/app-configuration';
import { MapMarkerConstants } from 'projects/uzurv/src/constants';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ReservationEventService, ReservationEventType } from '../../reservation-event';

@Component({
  selector: 'app-reservation-detail-map',
  templateUrl: './reservation-detail-map.component.html',
  styleUrls: ['./reservation-detail-map.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ReservationDetailMapComponent implements OnChanges, OnDestroy {
  @Input() route: IMapRoute;
  @Input() completedRoute: IMapRoute;
  @Input() enRoute: IMapRoute;
  @Input() center: IGeoCoords;
  @Input() reservationId: number;
  @Input() height = '500px';
  @Input() showRouteDetails = true;
  @Input() rideRequestDetail: RideRequestDetail;
  @Input() reservationStatusName: string;

  @ViewChild('googleMap', { static: true }) private googleMap: ElementRef;

  routePolyline: google.maps.Polyline = null;
  completedRoutePolyline: google.maps.Polyline = null;
  enRoutePolyline: google.maps.Polyline = null;

  showRouteLayersContainer = false;

  private map: google.maps.Map;
  private trafficLayer: google.maps.TrafficLayer;
  private destroy = new Subject<boolean>();
  private reservationEventsServiceSubscription = new Subscription();
  private previousCoords: IGeoCoords;
  private bearing = 0;
  private driverCarSubscription: Subscription;
  private carMarker: google.maps.Marker;
  private startNodeMarker: google.maps.Marker;
  private endNodeMarker: google.maps.Marker;
  private mapMarkers: Array<google.maps.Marker> = [];
  private mapPolylines: Array<google.maps.Polyline> = [];

  constructor(
    private mapsService: MapsService,
    private reservationService: ReservationService,
    private distanceConversionProvider: DistanceConversionProvider,
    private timeConversionProvider: TimeConversionProvider,
    private reservationEventService: ReservationEventService,
    @Inject(APP_CONFIGURATION) private appConfiguration: AppConfiguration
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (isNullOrUndefined(this.map)) {
      this.map = this.mapsService.buildMap(this.googleMap.nativeElement, this.center, 10);
    }

    const rideRequestDetailChange: SimpleChange = changes['rideRequestDetail'];

    if (!isNullOrUndefined(rideRequestDetailChange)) {
      if (rideRequestDetailChange.currentValue?.id !== rideRequestDetailChange.previousValue?.id) {
        this.clearMapOverlays();
      }
    }

    if (changes['reservationId']) {
      // no need to recreate the car and position subscription if the reservation just polled
      if (changes['reservationId'].currentValue === changes['reservationId'].previousValue) {
        return;
      }

      this.clearMapOverlays();

      // Clear existing car marker
      if (!isNullOrUndefined(this.carMarker)) {
        this.cleanup();
        this.carMarker.setMap(null);
      }

      this.carMarker = undefined;

      if (!isNullOrUndefined(changes['reservationId'].currentValue)) {
        let color: string;

        this.reservationEventsServiceSubscription = this.reservationEventService.eventHandler.subscribe(event => {
          if (event.eventType === ReservationEventType.DetailsClosed) {
            this.destroy.next(true);
          }
        });

        if (this.shouldTrackDriverLocation()) {
          this.driverCarSubscription = this.reservationService
            .trackDriverLocation(changes['reservationId'].currentValue, this.appConfiguration.listPollingSeconds * 1000)
            .pipe(takeUntil(this.destroy))
            .subscribe(miniDriver => {
              if (miniDriver && !isNullOrUndefined(miniDriver.latitude) && !isNullOrUndefined(miniDriver.longitude)) {
                color = this.getDriverColor(miniDriver);

                if (this.carMarker === undefined) {
                  this.carMarker = this.mapsService.buildMarker(
                    { lng: miniDriver.longitude, lat: miniDriver.latitude },
                    this.map,
                    this.bearing
                  );
                  this.previousCoords = { lng: miniDriver.longitude, lat: miniDriver.latitude };
                }

                if (miniDriver.longitude !== this.previousCoords.lng && miniDriver.latitude !== this.previousCoords.lat) {
                  this.bearing = this.mapsService.calculateBearing(
                    { lng: miniDriver.longitude, lat: miniDriver.latitude },
                    this.previousCoords
                  );
                }

                this.carMarker.setIcon(this.mapsService.buildImage(this.bearing, color));
                this.previousCoords = { lng: miniDriver.longitude, lat: miniDriver.latitude };
                this.carMarker.setPosition(this.previousCoords);
              }
            });
        }
      }
    }

    if (changes['reservationStatusName']) {
      if (!this.shouldTrackDriverLocation() && !isNullOrUndefined(this.driverCarSubscription)) {
        this.driverCarSubscription.unsubscribe();
      }
    }

    // If we've already created a car marker, add it to the new map
    if (!isNullOrUndefined(this.carMarker)) {
      this.carMarker.setMap(this.map);
      this.mapMarkers.push(this.carMarker);
    }

    if (!isNullOrUndefined(changes['route'])) {
      this.handleRouteChange(changes['route']);
    }

    if (!isNullOrUndefined(changes['enRoute'])) {
      this.handleEnRouteChange(changes['enRoute']);
    }

    if (!isNullOrUndefined(changes['completedRoute'])) {
      this.handleCompletedRouteChange(changes['completedRoute']);
    }
  }

  ngOnDestroy() {
    this.cleanup();
  }

  // Map Overlay Management

  private handleRouteChange(routeChange: SimpleChange) {
    if (isNullOrUndefined(routeChange)) {
      return;
    }

    const previousValue: IMapRoute = routeChange.previousValue as IMapRoute;
    const currentValue: IMapRoute = routeChange.currentValue as IMapRoute;

    const polylineUpdateRequired =
      currentValue?.reservationId !== previousValue?.reservationId ||
      currentValue?.polyline !== previousValue?.polyline ||
      currentValue?.distance !== previousValue?.distance;

    if (polylineUpdateRequired) {
      // We have a previous value that needs to be cleared
      if (!isNullOrUndefined(previousValue)) {
        this.clearMapPolyline(this.routePolyline);
        this.clearMapMarker(this.startNodeMarker);
        this.clearMapMarker(this.endNodeMarker);

        this.routePolyline = null;
        this.startNodeMarker = null;
        this.endNodeMarker = null;
      }

      // Handle change
      if (!isNullOrUndefined(this.route)) {
        let startInfo: string;
        let endInfo: string;

        if (this.route.startAddress) {
          startInfo = `<h5>Pick-up</h5>
                        <p>${this.route.startAddress}</p>`;
        }

        if (this.route.endAddress) {
          endInfo = `<h5>Drop-off</h5>
                      <p>${this.route.endAddress}</p>`;
        }

        this.startNodeMarker = this.mapsService.buildNodeMarker({
          location: this.route.startNode,
          map: this.map,
          color: 'green',
          info: startInfo
        });
        this.mapMarkers.push(this.startNodeMarker);

        this.endNodeMarker = this.mapsService.buildNodeMarker({ location: this.route.endNode, map: this.map, color: 'red', info: endInfo });
        this.mapMarkers.push(this.endNodeMarker);

        if (this.route?.polyline) {
          if (this.showRouteDetails) {
            this.routePolyline = this.mapsService.buildRoute(
              this.route,
              this.map,
              '#B4CEFD',
              `<h5>Route Details</h5>
             <p><strong>Distance: </strong>${this.distanceConversionProvider.convertMetersToMiles(this.route.distance, 1)} mi</p>
             <strong>Estimated Time: </strong>${this.timeConversionProvider.convertSecondsToMinutes(this.route.duration)} min`
            );
          } else {
            this.routePolyline = this.mapsService.buildRoute(this.route, this.map, '#B4CEFD');
          }

          this.mapPolylines.push(this.routePolyline);

          this.mapsService.fitMap(this.map, this.routePolyline);
        }
      }
    }
  }

  private shouldTrackDriverLocation(): boolean {
    return (
      !isNullOrUndefined(this.reservationStatusName) &&
      (this.reservationStatusName === ReservationStatusNameConstants.DRIVER_EN_ROUTE ||
        this.reservationStatusName === ReservationStatusNameConstants.PICK_UP_ARRIVED ||
        this.reservationStatusName === ReservationStatusNameConstants.IN_PROGRESS ||
        this.reservationStatusName === ReservationStatusNameConstants.DROP_OFF_ARRIVED)
    );
  }

  private handleEnRouteChange(enRouteChange: SimpleChange) {
    if (isNullOrUndefined(enRouteChange)) {
      return;
    }

    const previousValue: IMapRoute = enRouteChange.previousValue as IMapRoute;
    const currentValue: IMapRoute = enRouteChange.currentValue as IMapRoute;

    const polylineUpdateRequired = currentValue?.polyline !== previousValue?.polyline || currentValue?.distance !== previousValue?.distance;

    if (polylineUpdateRequired) {
      // We have a previous value that needs to be cleared
      if (!isNullOrUndefined(previousValue)) {
        this.clearMapPolyline(this.enRoutePolyline);
        this.enRoutePolyline = null;
      }

      // Handle change
      if (!isNullOrUndefined(this.enRoute?.polyline)) {
        this.enRoutePolyline = this.mapsService.buildRoute(this.enRoute, this.map, '#0553E1');
        this.mapPolylines.push(this.enRoutePolyline);
      }
    }
  }

  private handleCompletedRouteChange(completedRouteChange: SimpleChange) {
    if (isNullOrUndefined(completedRouteChange)) {
      return;
    }

    const previousValue: IMapRoute = completedRouteChange.previousValue as IMapRoute;
    const currentValue: IMapRoute = completedRouteChange.currentValue as IMapRoute;

    const polylineUpdateRequired = currentValue?.polyline !== previousValue?.polyline || currentValue?.polyline !== previousValue?.polyline;

    if (polylineUpdateRequired) {
      // We have a previous value that needs to be cleared
      if (!isNullOrUndefined(previousValue)) {
        this.clearMapPolyline(this.completedRoutePolyline);
        this.completedRoutePolyline = null;
      }

      // Handle change
      if (!isNullOrUndefined(this.completedRoute?.polyline)) {
        this.completedRoutePolyline = this.mapsService.buildRoute(this.completedRoute, this.map, '#010919');
        this.mapPolylines.push(this.completedRoutePolyline);
      }
    }
  }

  private clearMapOverlays() {
    this.mapMarkers?.forEach(mapMarker => {
      this.clearMapMarker(mapMarker);
    });

    this.mapMarkers = [];

    if (!isNullOrUndefined(this.carMarker)) {
      this.carMarker.setMap(null);
    }

    if (!isNullOrUndefined(this.driverCarSubscription)) {
      this.driverCarSubscription.unsubscribe();
    }

    this.carMarker = null;

    this.mapPolylines?.forEach(mapPolyline => {
      this.clearMapPolyline(mapPolyline);
    });

    this.mapPolylines = [];

    this.routePolyline = null;
    this.enRoutePolyline = null;
    this.completedRoutePolyline = null;
  }

  private clearMapMarker(mapMarker: google.maps.Marker) {
    if (isNullOrUndefined(mapMarker)) {
      return;
    }

    mapMarker.setMap(null);

    if (!isNullOrUndefined(mapMarker.get('infoWindow'))) {
      const infoWindow: google.maps.InfoWindow = mapMarker.get('infoWindow');
      infoWindow.close();
    }

    this.mapMarkers = this.mapMarkers.filter(mapMarker => !isNullOrUndefined(mapMarker.getMap()));
  }

  private clearMapPolyline(mapPolyline: google.maps.Polyline) {
    if (isNullOrUndefined(mapPolyline)) {
      return;
    }

    mapPolyline.setMap(null);

    if (!isNullOrUndefined(mapPolyline.get('infoWindow'))) {
      const infoWindow: google.maps.InfoWindow = mapPolyline.get('infoWindow');
      infoWindow.close();
    }

    this.mapPolylines = this.mapPolylines.filter(polyline => !isNullOrUndefined(polyline.getMap()));
  }

  toggleMapPolyline(mapPolyline: google.maps.Polyline) {
    if (!isNullOrUndefined(mapPolyline)) {
      mapPolyline.setVisible(!mapPolyline.getVisible());

      if (!isNullOrUndefined(mapPolyline.get('infoWindow'))) {
        const infoWindow: google.maps.InfoWindow = mapPolyline.get('infoWindow');

        if (mapPolyline.getVisible()) {
          infoWindow.open(this.map);
        } else {
          infoWindow.close();
        }
      }
    }
  }

  toggleTraffic(event) {
    if (event.checked === true) {
      this.trafficLayer = new google.maps.TrafficLayer();
      this.trafficLayer.setMap(this.map);
    } else {
      this.trafficLayer.setMap(null);
    }
  }

  toggleEstimate(): void {
    this.toggleMapPolyline(this.routePolyline);
  }

  toggleEnRoute(): void {
    this.toggleMapPolyline(this.enRoutePolyline);
  }

  toggleCompleted(): void {
    this.toggleMapPolyline(this.completedRoutePolyline);
  }

  handleShowPredictedRoute(): void {
    if (this.rideRequestDetail) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&origin=${this.rideRequestDetail.pickupLocation.display}&destination=${this.rideRequestDetail.dropoffLocation.display}`,
        '_blank'
      );
    }
  }

  private getDriverColor(miniDriver: MiniDriver) {
    switch (miniDriver.driverStatusName) {
      case DriverStatusConstants.EN_ROUTE:
        return MapMarkerConstants.DRIVER_MARKER_COLOR_EN_ROUTE;
      case DriverStatusConstants.PICK_UP_ARRIVED:
        return MapMarkerConstants.DRIVER_MARKER_COLOR_PICK_UP_ARRIVED;
      case DriverStatusConstants.IN_PROGRESS:
        return MapMarkerConstants.DRIVER_MARKER_COLOR_IN_PROGRESS;
      case DriverStatusConstants.DROP_OFF_ARRIVED:
        return MapMarkerConstants.DRIVER_MARKER_COLOR_DROP_OFF_ARRIVED;
    }

    if (miniDriver.driverStatusName === DriverStatusConstants.PERIOD_ONE) {
      return MapMarkerConstants.DRIVER_MARKER_COLOR_PERIOD1;
    }

    return MapMarkerConstants.DRIVER_MARKER_COLOR_INACTIVE;
  }

  private cleanup() {
    this.destroy.next(true);
    this.reservationEventsServiceSubscription.unsubscribe();
  }
}
